// ─────────────────────────────────────────────────────────────────
// THE PARENT PORTAL'S LOOK
//
// The same blue and white the staff app runs on. The portal used to be
// gold, borrowed from a banking app; against the school's own screens
// it read as a different product, and a parent who has seen the school
// office's system should recognise the portal as the same thing.
//
// WHY THE GROUND IS TINTED, NOT FLAT
//   A flat neutral grey behind a saturated hero card reads as
//   unfinished — a bright object dropped on a blank sheet, with large
//   empty areas that look like something failed to load rather than
//   like space. Two low-contrast moves fix it without adding noise:
//
//     1. The ground carries a trace of the brand hue, so cards sit ON
//        something rather than floating.
//     2. A soft bloom at the top, gone by about a third of the way
//        down. It gives the screen a light source, which is what makes
//        a flat layout read as designed.
//
//   Both are deliberately faint. This is a page parents read balances
//   on; decoration that competes with a number is a bug.
//
// NAMING
//   Tokens are named for their ROLE (brand, brandDeep, brandSoft), not
//   their colour. The first version called them `gold`, and the moment
//   the palette changed every name in five files was a lie.
// ─────────────────────────────────────────────────────────────────

export const P = {
  // Matches the staff theme's primary / secondary exactly.
  brand:      '#1565C0',
  brandDeep:  '#0D47A1',
  brandLight: '#2196F3',
  brandSoft:  '#E8F1FC',   // tinted panels — quick tiles, active rail item
  brandFaint: '#F5F9FE',   // barely there; hover grounds

  ink:        '#12213A',   // blue-black, so text belongs to the palette
  inkSoft:    '#5B6B84',

  bg:         '#F3F6FB',   // cool off-white ground
  bgDeep:     '#E8EEF7',
  card:       '#FFFFFF',
  line:       '#E2E9F3',   // hairline that shares the ground's hue

  good:       '#15803D',
  bad:        '#C2321F',

  // Text sitting ON the blue hero card. Kept here so the card and
  // anything else placed on brand colour agree about contrast.
  onBrand:      '#FFFFFF',
  onBrandSoft:  'rgba(255,255,255,.78)',
  onBrandFaint: 'rgba(255,255,255,.55)',
  onBrandGood:  '#A7F3C4',   // credit, legible on blue
  onBrandBad:   '#FFC9C2',   // owing, legible on blue
};

// The page's light source. A wide, soft blue bloom anchored to the top,
// plus a second wash lower down so the bottom of a long page is not
// identical to the top. Layered CSS gradients — no image, no request,
// and it scales to any viewport.
export const PAGE_BG = [
  `radial-gradient(1100px 420px at 50% -150px, rgba(33,150,243,.16), rgba(33,150,243,0) 70%)`,
  `radial-gradient(760px 380px at 100% 6%, rgba(21,101,192,.09), rgba(21,101,192,0) 70%)`,
  `radial-gradient(900px 520px at 0% 48%, rgba(13,71,161,.06), rgba(13,71,161,0) 70%)`,
  `linear-gradient(180deg, #F8FBFF 0%, ${P.bg} 36%, ${P.bg} 100%)`,
].join(', ');

// The rail on desktop. The SAME blue gradient the staff sidebar uses —
// a parent and a member of staff should recognise the two as one
// product, and the menu is the part of the frame they both look at all
// day. Angled at 170deg like the original so the darkest point sits
// top-left, matching the light source the page bloom implies.
export const RAIL_BG =
  `linear-gradient(170deg, ${'#0D47A1'} 0%, ${'#1565C0'} 55%, rgba(21,101,192,.88) 100%)`;

// Text and states ON the rail. Named separately from `onBrand` because
// the rail is a persistent surface with hover and active states, while
// the hero card is a single block — folding them together would mean
// changing one and silently moving the other.
export const RAIL = {
  text:       'rgba(255,255,255,.82)',
  textActive: '#FFFFFF',
  label:      'rgba(255,255,255,.55)',   // the "PARENT PORTAL" caption
  hover:      'rgba(255,255,255,.10)',
  active:     'rgba(255,255,255,.18)',
  activeEdge: '#FFFFFF',
  danger:     '#FFB4A6',                 // sign-out on hover
};

// Cards. Two shadows rather than one: a tight contact shadow that sits
// the card on the page, and a wide soft one that gives it height. A
// single mid shadow reads as a grey box behind a white box.
export const CARD_SHADOW =
  '0 1px 2px rgba(18,33,58,.05), 0 12px 28px -18px rgba(18,33,58,.26)';
export const CARD_SHADOW_LIFT =
  '0 2px 4px rgba(18,33,58,.07), 0 20px 40px -20px rgba(18,33,58,.32)';

// The hero card — the balance panel, and the header on the receipts
// page. Same gradient the staff dashboard's welcome band uses, so the
// two products look like one.
export const HERO_BG = [
  `radial-gradient(620px 280px at 8% -40%, rgba(255,255,255,.24), rgba(255,255,255,0) 62%)`,
  `linear-gradient(120deg, ${P.brandDeep} 0%, ${P.brand} 48%, ${P.brandLight} 100%)`,
].join(', ');

export const HERO_SHADOW =
  '0 2px 6px rgba(13,71,161,.20), 0 22px 44px -22px rgba(13,71,161,.70)';

// The faint geometric wash across the hero card. An inline SVG data URI
// rather than an asset: it is a handful of lines, and a file here would
// be one more thing to deploy. White strokes, because it now sits on a
// dark ground rather than a light one.
export const CARD_PATTERN =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' `
  + `viewBox='0 0 120 120'%3E%3Cg fill='none' stroke='%23fff' stroke-opacity='0.07' stroke-width='1.4'%3E`
  + `%3Cpath d='M0 30 L30 0 M0 60 L60 0 M0 90 L90 0 M0 120 L120 0 M30 120 L120 30 M60 120 L120 60 M90 120 L120 90'/%3E`
  + `%3C/g%3E%3C/svg%3E")`;

export const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

// Balances are shown as a magnitude plus a word, never as a bare minus
// sign. "GHS 45.00 owing" is unambiguous on a small screen in bright
// sun; "-45.00" is a rendering accident waiting to be misread.
export const balanceParts = (b) => {
  const v = Number(b || 0);
  if (v < 0)  return { amount: money(Math.abs(v)), tone: 'bad',  word: 'owing' };
  if (v > 0)  return { amount: money(v),           tone: 'good', word: 'in credit' };
  return { amount: money(0), tone: 'flat', word: 'settled' };
};
